import { useState, useEffect, useMemo, useCallback } from 'react';
import { BuildingSummary, CoordinatesModel, GroupTag } from '../../types/protocols';
import { ALL_BUILDING_TYPES, BuildingType } from '../../types/BuildingTypeEnums';
import { CustomDropdownOption } from '../../Components/Dropdown';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { translate } from '../../types/EnumTranslations';
import { BuildingTypeLabelOption } from '../../Helpers/buildingTypeOptions';
import { useBuildingTypeFilter } from '../useEnabledBuildingTypes';
import {
    LocationCategoriesConfig,
    LocationCategoryConfig,
    getLocationCategories,
    getBuildingTags,
} from '../../api/mapConfigApi';
import {
    saveToInstanceStorage,
    loadFromInstanceStorage,
    getInstanceStorageKey,
} from '../../Helpers/instanceStorage';
import {
    SortOption,
    SortOptionItem,
    SORT_OPTIONS,
    DEFAULT_SORT,
    BuildingTagSection,
    sortBuildingsFlat,
    groupBuildingsByTag,
    groupBuildingsByType,
    isUntagged,
    UNTAGGED_OPTION_VALUE,
} from './buildingSort';

// ── Module-level constants ──────────────────────────────────────────

const STORAGE_KEYS = {
    CATEGORY_1: 'mapOverview_selectedCategory1',
    CATEGORY_2: 'mapOverview_selectedCategory2',
    CATEGORY_3: 'mapOverview_selectedCategory3',
    CATEGORY_4: 'mapOverview_selectedCategory4',
    CATEGORY_5: 'mapOverview_selectedCategory5',
    BUILDING_TYPES: 'mapOverview_selectedBuildingTypes',
    GROUP_TAGS: 'mapOverview_selectedGroupTags',
    SORT: 'mapOverview_selectedSort',
    SHOW_FILTERED_MARKERS: 'mapOverview_showFilteredMarkers',
    IS_SATELLITE: 'mapOverview_isSatellite',
};

// Every type, not just the offered ones: a type enabled later starts selected.
const DEFAULT_BUILDING_TYPES: BuildingType[] = [...ALL_BUILDING_TYPES];

// Export storage utilities for use by MapComponent
export { saveToInstanceStorage as saveToStorage, loadFromInstanceStorage as loadFromStorage, getInstanceStorageKey, STORAGE_KEYS };

function getOptions(category: LocationCategoryConfig | undefined, parentKey?: string): CustomDropdownOption[] {
    if (!category) return [];
    return Object.entries(category)
        .filter(([_, value]) => value.parent === parentKey)
        .sort(([, a], [, b]) => a.name.localeCompare(b.name))
        .map(([key, value]) => ({ key, name: value.name }));
}

function filterBuildings(
    allBuildings: BuildingSummary[],
    filters: {
        selectedCategory1: string;
        selectedCategory2: string;
        selectedCategory3: string;
        selectedCategory4: string;
        selectedCategory5: string;
        selectedBuildingTypes: BuildingType[];
        selectedGroupTagKeys: string[] | null; // null = no preference yet (show all)
        totalAvailableGroupTags: number;
    },
): BuildingSummary[] {
    return allBuildings
        .filter((building) => {
            const categories = building.locationCategories;

            const categoryMatch =
                (!filters.selectedCategory1 || categories?.category1?.key === filters.selectedCategory1) &&
                (!filters.selectedCategory2 || categories?.category2?.key === filters.selectedCategory2) &&
                (!filters.selectedCategory3 || categories?.category3?.key === filters.selectedCategory3) &&
                (!filters.selectedCategory4 || categories?.category4?.key === filters.selectedCategory4) &&
                (!filters.selectedCategory5 || categories?.category5?.key === filters.selectedCategory5);

            const buildingTypeMatch =
                filters.selectedBuildingTypes.length > 0 &&
                building.buildingType !== undefined &&
                filters.selectedBuildingTypes.includes(building.buildingType);

            // null = no preference yet, show all buildings
            if (filters.selectedGroupTagKeys === null) {
                return categoryMatch && buildingTypeMatch;
            }

            const hasUntaggedOption = filters.selectedGroupTagKeys.includes(UNTAGGED_OPTION_VALUE);
            const regularTagKeys = filters.selectedGroupTagKeys.filter((key) => key !== UNTAGGED_OPTION_VALUE);
            const allRegularTagsSelected =
                filters.totalAvailableGroupTags > 0 && regularTagKeys.length === filters.totalAvailableGroupTags;

            const buildingIsUntagged = isUntagged(building);

            let groupTagMatch: boolean;
            if (filters.totalAvailableGroupTags === 0) {
                groupTagMatch = true;
            } else if (allRegularTagsSelected && hasUntaggedOption) {
                groupTagMatch = true;
            } else if (allRegularTagsSelected && !hasUntaggedOption) {
                // All tags checked but "Utan grupp" unchecked: hide untagged
                // buildings (matches the Förvaltning page's tag filter).
                groupTagMatch = !buildingIsUntagged;
            } else if (hasUntaggedOption && regularTagKeys.length === 0) {
                groupTagMatch = buildingIsUntagged;
            } else if (hasUntaggedOption && regularTagKeys.length > 0) {
                groupTagMatch =
                    buildingIsUntagged ||
                    (!!building.groupTags &&
                        Object.keys(building.groupTags).some((tagKey) => regularTagKeys.includes(tagKey)));
            } else {
                groupTagMatch =
                    !!building.groupTags &&
                    Object.keys(building.groupTags).some((tagKey) => regularTagKeys.includes(tagKey));
            }

            return categoryMatch && buildingTypeMatch && groupTagMatch;
        });
}

// ── Types ───────────────────────────────────────────────────────────

export type BuildingTypeOption = BuildingTypeLabelOption;

export interface GroupTagOption {
    label: string;
    value: string;
}

export interface AllCoordinateItem {
    coords: [number, number];
    building: BuildingSummary;
    isFiltered: boolean;
}

export interface UseMapOverviewReturn {
    loading: boolean;

    locationCategories: LocationCategoriesConfig | null;

    buildings: BuildingSummary[] | undefined;
    filteredBuildings: BuildingSummary[];
    buildingGroups: BuildingTagSection[] | null;

    selectedCategory1: string;
    selectedCategory2: string;
    selectedCategory3: string;
    selectedCategory4: string;
    selectedCategory5: string;
    selectedBuildingTypes: BuildingType[];
    selectedGroupTagKeys: string[];
    selectedSort: SortOption;

    selectCategory: (level: number, key: string) => void;
    setSelectedBuildingTypes: (types: BuildingType[]) => void;
    setSelectedGroupTagKeys: (keys: string[]) => void;
    setSelectedSort: (sort: SortOption) => void;
    resetAllFilters: () => void;
    hasAnyFilters: boolean;

    categoryOptions: CustomDropdownOption[][];
    buildingTypeOptions: BuildingTypeOption[];
    groupTagOptions: GroupTagOption[];
    sortOptions: SortOptionItem[];

    filteredCoordinates: { coords: [number, number] }[];
    allCoordinates: AllCoordinateItem[];
}

// ── Hook ────────────────────────────────────────────────────────────

export function useMapOverview(): UseMapOverviewReturn {
    // Config state
    const [locationCategories, setLocationCategories] = useState<LocationCategoriesConfig | null>(null);
    const [availableGroupTags, setAvailableGroupTags] = useState<GroupTag[]>([]);

    // Buildings from context
    const { buildings, loadingSummaries: buildingsLoading } = useBuildingsData();

    // Filter state with localStorage initialization
    const [selectedCategory1, setSelectedCategory1] = useState<string>(() =>
        loadFromInstanceStorage(STORAGE_KEYS.CATEGORY_1, ''),
    );
    const [selectedCategory2, setSelectedCategory2] = useState<string>(() =>
        loadFromInstanceStorage(STORAGE_KEYS.CATEGORY_2, ''),
    );
    const [selectedCategory3, setSelectedCategory3] = useState<string>(() =>
        loadFromInstanceStorage(STORAGE_KEYS.CATEGORY_3, ''),
    );
    const [selectedCategory4, setSelectedCategory4] = useState<string>(() =>
        loadFromInstanceStorage(STORAGE_KEYS.CATEGORY_4, ''),
    );
    const [selectedCategory5, setSelectedCategory5] = useState<string>(() =>
        loadFromInstanceStorage(STORAGE_KEYS.CATEGORY_5, ''),
    );
    const [selectedBuildingTypes, setSelectedBuildingTypesState] = useState<BuildingType[]>(() =>
        loadFromInstanceStorage(STORAGE_KEYS.BUILDING_TYPES, DEFAULT_BUILDING_TYPES),
    );
    // null = no preference saved yet (first time user), [] = user explicitly deselected all
    const [selectedGroupTagKeys, setSelectedGroupTagKeysState] = useState<string[] | null>(() =>
        loadFromInstanceStorage<string[] | null>(STORAGE_KEYS.GROUP_TAGS, null),
    );

    // Sort selection — persisted like the filters, defaults to name A–Ö. A stored
    // value that's no longer a valid option (e.g. a removed sort) falls back to the
    // default rather than leaving the control blank.
    const [selectedSort, setSelectedSortState] = useState<SortOption>(() => {
        const stored = loadFromInstanceStorage<SortOption>(STORAGE_KEYS.SORT, DEFAULT_SORT);
        return SORT_OPTIONS.some((option) => option.key === stored) ? stored : DEFAULT_SORT;
    });

    // ── Data fetching ───────────────────────────────────────────────

    useEffect(() => {
        getLocationCategories().then((data) => setLocationCategories(data));
        getBuildingTags().then((tags) => setAvailableGroupTags(tags));
    }, []);

    // ── localStorage persistence ────────────────────────────────────

    useEffect(() => { saveToInstanceStorage(STORAGE_KEYS.CATEGORY_1, selectedCategory1); }, [selectedCategory1]);
    useEffect(() => { saveToInstanceStorage(STORAGE_KEYS.CATEGORY_2, selectedCategory2); }, [selectedCategory2]);
    useEffect(() => { saveToInstanceStorage(STORAGE_KEYS.CATEGORY_3, selectedCategory3); }, [selectedCategory3]);
    useEffect(() => { saveToInstanceStorage(STORAGE_KEYS.CATEGORY_4, selectedCategory4); }, [selectedCategory4]);
    useEffect(() => { saveToInstanceStorage(STORAGE_KEYS.CATEGORY_5, selectedCategory5); }, [selectedCategory5]);
    useEffect(() => { saveToInstanceStorage(STORAGE_KEYS.BUILDING_TYPES, selectedBuildingTypes); }, [selectedBuildingTypes]);
    // Only persist group tags after user has made a selection (not null)
    useEffect(() => {
        if (selectedGroupTagKeys !== null) {
            saveToInstanceStorage(STORAGE_KEYS.GROUP_TAGS, selectedGroupTagKeys);
        }
    }, [selectedGroupTagKeys]);
    useEffect(() => { saveToInstanceStorage(STORAGE_KEYS.SORT, selectedSort); }, [selectedSort]);

    // ── Tag validation ──────────────────────────────────────────────

    useEffect(() => {
        if (availableGroupTags.length === 0) return;

        const availableTagKeys = new Set(availableGroupTags.map((tag) => tag.key));

        setSelectedGroupTagKeysState((currentSelected) => {
            // First time user: select all tags
            if (currentSelected === null) {
                return [UNTAGGED_OPTION_VALUE, ...availableGroupTags.map((tag) => tag.key)];
            }

            // Validate existing selection against available tags
            const hasUntagged = currentSelected.includes(UNTAGGED_OPTION_VALUE);
            const regularTags = currentSelected.filter((key) => key !== UNTAGGED_OPTION_VALUE);
            const validRegularTags = regularTags.filter((key) => availableTagKeys.has(key));

            if (validRegularTags.length !== regularTags.length) {
                return hasUntagged ? [UNTAGGED_OPTION_VALUE, ...validRegularTags] : validRegularTags;
            }
            return currentSelected;
        });
    }, [availableGroupTags]);

    // ── Actions ─────────────────────────────────────────────────────

    const selectCategory = useCallback((level: number, key: string) => {
        const setters = [setSelectedCategory1, setSelectedCategory2, setSelectedCategory3, setSelectedCategory4, setSelectedCategory5];
        // Set the selected level
        setters[level - 1](key);
        // Reset all levels after the selected one
        for (let i = level; i < 5; i++) {
            setters[i]('');
        }
    }, []);


    const setSelectedGroupTagKeys = useCallback((keys: string[]) => {
        setSelectedGroupTagKeysState(keys);
    }, []);

    const setSelectedSort = useCallback((sort: SortOption) => {
        setSelectedSortState(sort);
    }, []);

    const resetAllFilters = useCallback(() => {
        setSelectedCategory1('');
        setSelectedCategory2('');
        setSelectedCategory3('');
        setSelectedCategory4('');
        setSelectedCategory5('');
        setSelectedBuildingTypesState(DEFAULT_BUILDING_TYPES);
        const allTagKeys = [UNTAGGED_OPTION_VALUE, ...availableGroupTags.map((tag) => tag.key)];
        setSelectedGroupTagKeysState(allTagKeys);
    }, [availableGroupTags]);

    // ── Derived data (useMemo) ──────────────────────────────────────

    // The filter offers the enabled types plus those a building still has; the
    // stored selection may hold others, which match no building and aren't shown.
    const typesInUse = useMemo(() => (buildings ?? []).map((b) => b.buildingType), [buildings]);
    const {
        options: buildingTypeOptions,
        selected: visibleSelectedBuildingTypes,
        setSelected: setSelectedBuildingTypes,
    } = useBuildingTypeFilter(selectedBuildingTypes, setSelectedBuildingTypesState, typesInUse);

    const filteredBuildings = useMemo(() => {
        if (!buildings) return [];
        const filtered = filterBuildings(buildings, {
            selectedCategory1,
            selectedCategory2,
            selectedCategory3,
            selectedCategory4,
            selectedCategory5,
            selectedBuildingTypes,
            selectedGroupTagKeys,
            totalAvailableGroupTags: availableGroupTags.length,
        });
        // group_by_tag has no flat ordering — sortBuildingsFlat falls back to
        // name A–Ö, which is what the map (order-agnostic) and the section
        // builder both want as a base.
        return sortBuildingsFlat(filtered, selectedSort, translate);
    }, [
        buildings,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedCategory5,
        selectedBuildingTypes,
        selectedGroupTagKeys,
        availableGroupTags.length,
        selectedSort,
    ]);

    // Restricts which tags become sections when grouping. `null` means no active
    // group-tag filter, so the grouped view shows every tag the buildings carry
    // (including any not present in the master buildingTags list). A Set limits
    // sections to the actively-selected tags, so a multi-tag building appears only
    // under its selected tags (R14). This mirrors filterBuildings: when all tags
    // are selected the filter passes everything, so grouping should too.
    const groupTagRestriction = useMemo<Set<string> | null>(() => {
        if (selectedGroupTagKeys === null) return null;
        const regularKeys = selectedGroupTagKeys.filter((key) => key !== UNTAGGED_OPTION_VALUE);
        const hasUntagged = selectedGroupTagKeys.includes(UNTAGGED_OPTION_VALUE);
        const allRegularSelected =
            availableGroupTags.length > 0 && regularKeys.length === availableGroupTags.length;
        if (availableGroupTags.length === 0 || (allRegularSelected && hasUntagged)) return null;
        return new Set(regularKeys);
    }, [selectedGroupTagKeys, availableGroupTags]);

    // Sections for the "group by tag" and "group by building type" modes;
    // null for the flat name_asc sort.
    const buildingGroups = useMemo<BuildingTagSection[] | null>(() => {
        if (selectedSort === 'group_by_tag') {
            // Always offer the untagged section — groupBuildingsByTag omits it when no
            // untagged building survived filtering. Tying its presence to the data
            // (rather than the untagged filter checkbox) keeps the grouped view
            // consistent with the map and flat sorts, which still show untagged
            // buildings in states where the filter admits them (e.g. all regular tags
            // selected with "Byggnader utan grupp" unchecked).
            return groupBuildingsByTag(filteredBuildings, groupTagRestriction, true);
        }
        if (selectedSort === 'building_type') {
            return groupBuildingsByType(filteredBuildings, translate);
        }
        return null;
    }, [selectedSort, filteredBuildings, groupTagRestriction]);

    const filteredCoordinates = useMemo(
        () =>
            filteredBuildings
                .filter(
                    (b): b is BuildingSummary & { coordinates: CoordinatesModel } =>
                        b.coordinates !== undefined && b.coordinates !== null,
                )
                .map((b) => ({
                    coords: [b.coordinates.latitude, b.coordinates.longitude] as [number, number],
                })),
        [filteredBuildings],
    );

    const allCoordinates = useMemo(() => {
        if (!buildings) return [];

        // Pre-compute filtered keys set for O(1) lookups instead of O(n) .some() per building
        const filteredKeys = new Set(filteredBuildings.map(fb => fb.key));

        return buildings
            .filter((b) => b.coordinates)
            .map((b) => ({
                coords: [b.coordinates!.latitude, b.coordinates!.longitude] as [number, number],
                building: b,
                isFiltered: filteredKeys.has(b.key),
            }));
    }, [buildings, filteredBuildings]);

    const categoryOptions = useMemo<CustomDropdownOption[][]>(
        () => [
            getOptions(locationCategories?.category1),
            getOptions(locationCategories?.category2, selectedCategory1),
            getOptions(locationCategories?.category3, selectedCategory2),
            getOptions(locationCategories?.category4, selectedCategory3),
            getOptions(locationCategories?.category5, selectedCategory4),
        ],
        [locationCategories, selectedCategory1, selectedCategory2, selectedCategory3, selectedCategory4],
    );

    const groupTagOptions = useMemo<GroupTagOption[]>(
        () => [
            { label: 'Byggnader utan grupp', value: UNTAGGED_OPTION_VALUE },
            ...availableGroupTags.map((tag) => ({ label: tag.tag, value: tag.key })),
        ],
        [availableGroupTags],
    );

    const hasAnyFilters = useMemo(() => {
        // null = no preference yet, means "show all" so no filter active
        if (selectedGroupTagKeys === null) {
            return (
                selectedCategory1 !== '' ||
                visibleSelectedBuildingTypes.length < buildingTypeOptions.length
            );
        }

        const regularTagKeys = selectedGroupTagKeys.filter((key) => key !== UNTAGGED_OPTION_VALUE);
        const allRegularTagsSelected =
            availableGroupTags.length > 0 && regularTagKeys.length === availableGroupTags.length;
        const hasUntaggedOption = selectedGroupTagKeys.includes(UNTAGGED_OPTION_VALUE);
        const groupTagsFilterActive = availableGroupTags.length > 0 && (!allRegularTagsSelected || !hasUntaggedOption);

        return (
            selectedCategory1 !== '' ||
            visibleSelectedBuildingTypes.length < buildingTypeOptions.length ||
            groupTagsFilterActive
        );
    }, [selectedCategory1, visibleSelectedBuildingTypes, buildingTypeOptions.length, selectedGroupTagKeys, availableGroupTags]);

    const loading = !locationCategories || buildingsLoading;

    return {
        loading,
        locationCategories,
        buildings,
        filteredBuildings,
        buildingGroups,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedCategory5,
        selectedBuildingTypes: visibleSelectedBuildingTypes,
        // Coerce null to empty array for external interface (null only exists briefly during load)
        selectedGroupTagKeys: selectedGroupTagKeys ?? [],
        selectedSort,
        selectCategory,
        setSelectedBuildingTypes,
        setSelectedGroupTagKeys,
        setSelectedSort,
        resetAllFilters,
        hasAnyFilters,
        categoryOptions,
        buildingTypeOptions,
        groupTagOptions,
        sortOptions: SORT_OPTIONS,
        filteredCoordinates,
        allCoordinates,
    };
}
